import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { BrandSchema, BrandSchemaType } from '../../validations/brand';
import { clientSchema, ClientSchemaType } from '../../validations/client';
import { useThemeStore } from '../../state/theme';
import { BtnPrimary } from '../common/Buttons';
import { IBrand, IClient } from '../../types/brand';
import { addBrand, addClient, updateClient } from '../../hooks/mutations';
import { AddBrandResponse } from '../../api/brand/addBrand';
import Select from '../common/Select';
import { countryOptions } from '../../constants';
import PhoneNumberInput from '../common/PhoneNumberInput';
import { LoaderCircle } from 'lucide-react';
import { useResourceUpload } from '../../hooks/useResourceUpload';
import ResourceUploadSection from '../common/ResourceUploadSection';
import LeavePoliciesEditor, {
  PolicyPeriod,
  emptyPolicy,
  fromPolicyPeriods,
} from './LeavePoliciesEditor';

interface AddClientProps {
  onSuccess: (data: AddBrandResponse) => void;
  onError: (error: Error) => void;
  initialData?: IClient;
  isEditing?: boolean;
}

type FormStep = 'client' | 'brand';

const AddClient: React.FC<AddClientProps> = ({
  onSuccess,
  onError,
  initialData,
  isEditing,
}) => {
  const [currentStep, setCurrentStep] = useState<FormStep>('client');
  const [createdClient, setCreatedClient] = useState<string | null>(null);

  const [tolerancePeriod, setTolerancePeriod] = useState<number>(15);
  const [kimPolicies, setKimPolicies] = useState<PolicyPeriod[]>([emptyPolicy()]);
  const [brandPolicies, setBrandPolicies] = useState<PolicyPeriod[]>([emptyPolicy()]);

  // Resource upload hook
  const resourceUpload = useResourceUpload({
    categories: ['brand', 'scenario'],
    existingResources: [],
  });

  const { mutate: addBrandMutation, isPending: isAddingBrand } = addBrand({
    onSuccess: async (data) => {
      try {
        const brand = data?.brand;
        if (brand?._id) {
          await resourceUpload.uploadToServer(brand._id, (brand.resources ?? []).map((r) => ({ ...r, uploadedAt: r.uploadedAt ?? new Date() })));
        }
        onSuccess?.(data);
      } catch (err) {
        console.error('Resource upload failed:', err);
        onSuccess?.(data);
      }
    },
    onError: (e) => onError?.(e),
  });

  const { mutate: addClientMutation, isPending: isAddingClient } = addClient({
    onSuccess: (data) => {
      setCreatedClient((data as any)._id);
      setCurrentStep('brand');
    },
    onError: (e) => onError?.(e),
  });

  const { mutate: updateClientMutation, isPending: isUpdatingClient } =
    updateClient({
      onSuccess: (data) => onSuccess?.(data),
      onError: (e) => onError?.(e),
    });

  const { isDarkMode } = useThemeStore();

  // Client form
  const {
    register: registerClient,
    handleSubmit: handleSubmitClient,
    setValue: setClientValue,
    control: formClient,
    formState: { errors: clientErrors },
  } = useForm<ClientSchemaType>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: initialData?.name || '',
      businessName: initialData?.businessName || '',
      phoneNumber: initialData?.phoneNumber || {
        number: '',
        countryCode: '',
        countryCodeEnum: '',
      },
      email: initialData?.email || '',
      country: initialData?.country || '',
    },
  });

  // Brand form
  const {
    register: registerBrand,
    handleSubmit: handleSubmitBrand,
    formState: { errors: brandErrors },
    setValue: setBrandValue,
  } = useForm<BrandSchemaType>({
    resolver: zodResolver(BrandSchema),
    defaultValues: {
      clientId: '',
      name: '',
      websiteLink: '',
      onboardingDate: new Date().toISOString().split('T')[0],
      agents: [],
    },
  });

  // Update clientId when createdClient changes
  React.useEffect(() => {
    if (createdClient) {
      setBrandValue('clientId', createdClient);
    }
  }, [createdClient, setBrandValue]);

  const inputClassName = `w-full rounded-md px-3 py-2 shadow-sm focus:outline-none ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

  const labelClassName = `block text-sm font-medium mb-1 ${
    isDarkMode ? 'text-gray-300' : 'text-gray-700'
  }`;

  const onSubmitClient = (data: ClientSchemaType) => {
    if (isEditing) {
      const clientData = {
        ...data,
        _id: initialData?._id,
      };
      updateClientMutation(clientData);
    } else {
      addClientMutation(data);
    }
  };

  const onSubmitBrand = (data: BrandSchemaType) => {
    const allResources = [
      ...resourceUpload.resources.brand,
      ...resourceUpload.resources.scenario,
    ];

    const brandData: IBrand = {
      ...data,
      clientId: createdClient,
      resources: allResources,
      configuration: {
        tolerancePeriod,
        kimLeaves: fromPolicyPeriods(kimPolicies),
        brandLeaves: fromPolicyPeriods(brandPolicies),
      },
    };

    addBrandMutation(brandData);
  };

  return (
    <>
      {/* Progress indicator */}
      <div className={`${isEditing ? 'hidden' : 'mb-6'}`}>
        <div className="flex items-center justify-between">
          <div
            className={`flex items-center ${currentStep === 'client' ? 'text-indigo-600' : 'text-green-600'}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                currentStep === 'client' ? 'bg-indigo-600' : 'bg-green-600'
              }`}
            >
              {currentStep === 'client' ? '1' : '✓'}
            </div>
            <span className="ml-2 font-medium">Client Details</span>
          </div>
          <div
            className={`h-1 flex-1 mx-4 ${currentStep === 'brand' ? 'bg-green-200' : 'bg-gray-200'}`}
          ></div>
          <div
            className={`flex items-center ${currentStep === 'brand' ? 'text-indigo-600' : 'text-gray-400'}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                currentStep === 'brand' ? 'bg-indigo-600' : 'bg-gray-400'
              }`}
            >
              2
            </div>
            <span className="ml-2 font-medium">Brand Details</span>
          </div>
        </div>
      </div>

      {currentStep === 'client' && (
        <form
          onSubmit={handleSubmitClient(onSubmitClient)}
          className="space-y-6"
        >
          <h2
            className={`${isEditing ? 'hidden' : ''} text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
          >
            Create Client
          </h2>
          <div>
            <label htmlFor="businessName" className={labelClassName}>
              Client Name
            </label>
            <input
              id="businessName"
              {...registerClient('businessName')}
              className={inputClassName}
              placeholder="Enter client name"
            />
            {clientErrors.businessName && (
              <span className="text-red-500 text-sm">
                {clientErrors.businessName.message}
              </span>
            )}
          </div>
          <div>
            <label htmlFor="email" className={labelClassName}>
              Email
            </label>
            <input
              id="email"
              type="email"
              {...registerClient('email')}
              className={inputClassName}
              placeholder="Enter client email"
            />
            {clientErrors.email && (
              <span className="text-red-500 text-sm">
                {clientErrors.email.message}
              </span>
            )}
          </div>
          <div>
            <label htmlFor="name" className={labelClassName}>
              Point of Contact
            </label>
            <input
              id="name"
              {...registerClient('name')}
              className={inputClassName}
              placeholder="Enter point of contact"
            />
            {clientErrors.name && (
              <span className="text-red-500 text-sm">
                {clientErrors.name.message}
              </span>
            )}
          </div>
          <div>
            <label htmlFor="phoneNumber" className={labelClassName}>
              Phone Number
            </label>
            <Controller
              name="phoneNumber"
              control={formClient}
              defaultValue={
                initialData?.phoneNumber || {
                  number: '',
                  countryCode: '',
                  countryCodeEnum: 'us',
                }
              }
              render={({ field: { onChange, value } }) => (
                <PhoneNumberInput
                  defaultCountry={value.countryCodeEnum || 'us'}
                  value={value.number}
                  onChange={(phone, data) => {
                    onChange?.({
                      number: phone,
                      countryCode: data.country.dialCode,
                      countryCodeEnum: data.country.iso2,
                    });
                  }}
                />
              )}
            />
            {clientErrors.phoneNumber && (
              <span className="text-red-500 text-sm">
                {clientErrors.phoneNumber.message}
              </span>
            )}
          </div>

          <div>
            <label htmlFor="country" className={labelClassName}>
              Country
            </label>
            <Select
              isMulti={false}
              id="country"
              {...registerClient('country')}
              options={countryOptions}
              placeholder="Select country"
              value={countryOptions.find(
                (option) => option.value === initialData?.country
              )}
              onChange={(newValue) => {
                setClientValue('country', newValue?.value || '');
              }}
            />
            {clientErrors.country && (
              <span className="text-red-500 text-sm">
                {clientErrors.country.message}
              </span>
            )}
          </div>
          <div className="flex justify-end">
            <BtnPrimary
              disabled={isAddingClient || isUpdatingClient}
              type="submit"
            >
              {isAddingClient || isUpdatingClient ? (
                <div className="flex items-center gap-2">
                  <span>{isEditing ? 'Updating' : 'Creating'}</span>
                  <LoaderCircle size={16} className="animate-spin" />
                </div>
              ) : isEditing ? (
                'Update Client'
              ) : (
                'Create Client'
              )}
            </BtnPrimary>
          </div>
        </form>
      )}

      {currentStep === 'brand' && (
        <form onSubmit={handleSubmitBrand(onSubmitBrand)} className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h2
              className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
            >
              Create Brand
            </h2>
          </div>

          {createdClient && (
            <div
              className={`p-3 rounded-md ${isDarkMode ? 'bg-green-800' : 'bg-green-100'}`}
            >
              <p
                className={`text-sm ${isDarkMode ? 'text-green-200' : 'text-green-800'}`}
              >
                ✓ Client created successfully
              </p>
            </div>
          )}

          <div>
            <label htmlFor="name" className={labelClassName}>
              Brand Name
            </label>
            <input
              id="name"
              {...registerBrand('name')}
              className={inputClassName}
              placeholder="Enter brand name"
            />
            {brandErrors.name && (
              <span className="text-red-500 text-sm">
                {brandErrors.name.message}
              </span>
            )}
          </div>

          <div>
            <label htmlFor="link" className={labelClassName}>
              Website Link
            </label>
            <input
              id="websiteLink"
              {...registerBrand('websiteLink')}
              className={inputClassName}
              placeholder="https://example.com"
            />
            {brandErrors.websiteLink && (
              <span className="text-red-500 text-sm">
                {brandErrors.websiteLink.message}
              </span>
            )}
          </div>

          {/* Configuration Settings */}
          <div>
            <label htmlFor="tolerancePeriod" className={labelClassName}>
              Tolerance Period (minutes)
            </label>
            <input
              type="number"
              id="tolerancePeriod"
              value={tolerancePeriod}
              onChange={(e) => setTolerancePeriod(Number(e.target.value))}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              min="0"
              className={inputClassName}
            />
            <p
              className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}
            >
              Grace period for late clock-ins/outs
            </p>
          </div>

          {/* Leave Policies */}
          <div className="space-y-8">
            <LeavePoliciesEditor
              label="Kim Leave Policies"
              policies={kimPolicies}
              onChange={setKimPolicies}
              inputClassName={inputClassName}
              labelClassName={labelClassName}
              isDarkMode={isDarkMode}
            />
            <LeavePoliciesEditor
              label="Brand Leave Policies"
              policies={brandPolicies}
              onChange={setBrandPolicies}
              inputClassName={inputClassName}
              labelClassName={labelClassName}
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Brand Resources Section */}
          <div className="space-y-6">
            <ResourceUploadSection
              title="Upload Brand Documents"
              category="brand"
              resources={resourceUpload.resources.brand}
              onFilesChange={resourceUpload.handleNewFiles}
              onRemoveResource={resourceUpload.removeResource}
              onViewResource={resourceUpload.handleViewResource}
              isUploading={resourceUpload.isUploading.brand}
              accept=".pdf,.doc,.docx,.txt"
              maxSize={100}
              maxFiles={10}
            />

            <ResourceUploadSection
              title="Upload Scenario Documents"
              category="scenario"
              resources={resourceUpload.resources.scenario}
              onFilesChange={resourceUpload.handleNewFiles}
              onRemoveResource={resourceUpload.removeResource}
              onViewResource={resourceUpload.handleViewResource}
              isUploading={resourceUpload.isUploading.scenario}
              accept=".pdf,.doc,.docx,.txt"
              maxSize={100}
              maxFiles={10}
            />
          </div>

          <div className="flex justify-end">
            <BtnPrimary
              disabled={
                isAddingBrand ||
                resourceUpload.isUploading.brand ||
                resourceUpload.isUploading.scenario ||
                resourceUpload.isUploadingToServer
              }
              type="submit"
              className="flex items-center gap-2 py-2"
            >
              {isAddingBrand || resourceUpload.isUploadingToServer ? (
                <div className="flex items-center gap-2">
                  <span>
                    {resourceUpload.isUploadingToServer
                      ? 'Uploading...'
                      : 'Creating'}
                  </span>
                  <LoaderCircle size={16} className="animate-spin" />
                </div>
              ) : (
                'Create Brand'
              )}
            </BtnPrimary>
          </div>
        </form>
      )}
    </>
  );
};

export default AddClient;
